import styled from 'styled-components';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import SnowflakeSetupForm, { SnowflakeDetails } from './SnowflakeConnectionForm.component';
import TenantService from '@services/tenant/tenant.service';
import { useImmer } from 'use-immer';
import { SurveyAnswers } from '@services/config/config.service';
import produce from 'immer';
import { getErrorMessage } from '@services/errors.service';
import TenantConfigService from '@services/tenantConfig.service';
import { CopyBlock } from 'react-code-blocks';
import SnowflakeGrantScript from '@components/sql/DataTableAccess.component';
import { SnowflakeTableSelector } from '@pages/Sources/SnowflakeDataSource.page';
import Dropdown from '@components/form/Dropdown.component';
import DataModelInference from './DataModelInference.component';
import { DataModelInferenceResponse, RelationshipsResponse, MetricsResponse } from './DataModelInference.component';
import ApiService from '@services/api/api.service';
import BackgroundService from '@services/bg.service';
import { closeBuildModal, runBuild } from '@services/alert/alert.service';
import AsyncButton from '@components/button/AsyncButton.component';
import toast from '@services/toast.service';
import { useNavigate } from 'react-router-dom';
import { enterDraftMode, useIsInDraftMode } from '@stores/data.store';

interface UseCaseButtonProps {
    text: string;
    iconClass?: string; // Optional icon class for <i>
    active?: boolean;
    onClick?: () => void;
    className?: string;
    small?: boolean; // Optional small size
}

const UseCaseButton: React.FC<UseCaseButtonProps> = ({
    text,
    iconClass,
    active = false,
    onClick,
    className = '',
    small = false,
}) => {
    // Define size-dependent styles
    const padding = small ? '1.25rem 0.75rem' : '2.5rem 1rem';
    const minHeight = small ? '90px' : '180px';
    const fontSize = small ? '1rem' : '1.15rem';
    const iconFontSize = small ? '1.7rem' : '3rem';
    const iconMarginBottom = small ? '0.5rem' : '1rem';
    const lineHeight = small ? '1.1' : '1.15';

    return (
        <button
            type="button"
            className={`usecase-btn ${active ? 'active' : ''} ${className}`}
            onClick={onClick}
            style={{
                width: '100%',
                background: active ? 'var(--pliable-blue)' : '#fff',
                border: '1px solid var(--pliable-blue)',
                borderRadius: '5px',
                padding,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'background 0.2s, color 0.2s',
                lineHeight,
                minHeight,
                boxShadow: active ? '0 2px 12px 0 rgba(255,159,0,0.10)' : 'none',
            }}
        >
            {iconClass && (
                <i
                    className={iconClass}
                    style={{
                        fontSize: iconFontSize,
                        color: active ? '#fff' : 'var(--pliable-yellow)',
                        marginBottom: iconMarginBottom,
                        transition: 'color 0.2s',
                    }}
                />
            )}
            <span
                style={{
                    fontSize,
                    fontWeight: 600,
                    color: active ? '#fff' : 'var(--ct-body-color)',
                    textAlign: 'center',
                    lineHeight,
                }}
            >
                {text}
            </span>
        </button>
    );
};


const Onboarding2Page = () => {
    const [useCase, setUseCase] = useState<string>('');

    const [snowflakeManagement, setSnowflakeManagement] = useState<boolean|undefined>(undefined);
    const [pliableUseCase, setPliableUseCase] = useState<string[]>([]);
    const [pliableUseCaseOther, setPliableUseCaseOther] = useState<string>('');
    
    const [step, changeStep] = useState<number>(1);
    const [importJobId, setImportJobId] = useState<string | null>(null);
    const [importLoading, setImportLoading] = useState(false);
    const [importComplete, setImportComplete] = useState(false);
    const [importError, setImportError] = useState<string | null>(null);
    const [selectedTables, setSelectedTables] = useState<string[]>([]);
    const [dataModelSetupOption, setDataModelSetupOption] = useState<'ai' | 'manual' | null>(null);
    const inDraftMode = useIsInDraftMode();

    useEffect(() => {
        if (!inDraftMode) {
            enterDraftMode('onboarding');
        }
    }, [inDraftMode]);



    const snowflakeSetupStep = useMemo<'CREDS' | 'SCRIPT' | 'VALIDATE'>(() => {
        if (step === 2) return 'CREDS';
        if (step === 3) return 'SCRIPT';
        if (step === 4) return 'VALIDATE';
        return 'CREDS';
    }, [step]);

    const [snowflakeDetails, setSnowflakeDetails] = useState<SnowflakeDetails>({
        private_key: '',
        private_key_passphrase: '',
        account_identifier: '',
        username: 'PLIABLE_USER',
        database: 'PLIABLE_DATABASE',
        role: 'PLIABLE_ROLE',
        query_warehouse: 'PLIABLE_QUERY_WAREHOUSE',
        loading_warehouse: 'PLIABLE_LOADING_WAREHOUSE',
        build_warehouse: 'PLIABLE_BUILD_WAREHOUSE',
        loader_username: 'PLIABLE_LOADER_USER',
        loader_database: 'PLIABLE_LOADER_DATABASE',
        loader_role: 'PLIABLE_LOADER_ROLE',
    });

    const [snowflakeConnectionIsValid, setSnowflakeConnectionIsValid] = useState(false);
    const [dataModelInferenceComplete, setDataModelInferenceComplete] = useState(false);
    const [aiInferenceLoading, setAiInferenceLoading] = useState(false);
    
    // State variables for storing AI-inferred data
    const [inferredEntities, setInferredEntities] = useState<DataModelInferenceResponse | null>(null);
    const [inferredRelationships, setInferredRelationships] = useState<RelationshipsResponse | null>(null);
    const [inferredMetrics, setInferredMetrics] = useState<MetricsResponse | null>(null);

    const runDataModelImport = useCallback(async () => {
        setImportLoading(true);
        setImportError(null);
        
        try {
            // Prepare the request payload based on the data model setup option
            const requestPayload: any = {
                method: dataModelSetupOption,
                source_tables: selectedTables.map(tableName => ({
                    table_name: tableName,
                    columns: null // Let the backend describe the tables
                }))
            };
            
            // Add AI-specific payload if using AI method
            if (dataModelSetupOption === 'ai' && inferredEntities && inferredRelationships && inferredMetrics) {
                requestPayload.renaming_suggestions = {
                    tables: inferredEntities.tables.map(table => ({
                        source_table_name: table.source_table_name,
                        data_model_table_name: table.data_model_table_name,
                        columns: table.columns.map(col => ({
                            source_column_name: col.source_column_name,
                            recommended_column_name: col.recommended_column_name,
                            data_type: col.data_type,
                            use_as_dimension: col.use_as_dimension
                        }))
                    }))
                };
                requestPayload.relationship_suggestions = {
                    relationships: inferredRelationships.relationships
                };
                requestPayload.metric_suggestions = {
                    metrics: inferredMetrics.metrics
                };
            }
            
            // Call the onboarding import API
            const response = await ApiService.getInstance().request('POST', '/onboarding/import', requestPayload) as { job_id: string };
            
            setImportJobId(response.job_id);
            
            // Track the job status
            const result = await BackgroundService.getInstance().waitForJob(response.job_id);
            
            setImportComplete(true);
            console.log('Import completed successfully:', result);
            
        } catch (err) {
            console.error('Import failed:', err);
            setImportError(err instanceof Error ? err.message : 'Import failed');
        } finally {
            setImportLoading(false);
        }
    }, [selectedTables, dataModelSetupOption, inferredEntities, inferredRelationships, inferredMetrics]);

    const setStep = useCallback(async (step: number) => {
        changeStep(step);
        if (step === 6 && useCase === 'MODELED_DATA' && (dataModelSetupOption === 'manual' || dataModelSetupOption === 'ai')) {
            await runDataModelImport();
        }
    }, [useCase, dataModelSetupOption, runDataModelImport]);

    const handleDataModelInferenceComplete = useCallback((entities: DataModelInferenceResponse, relationships: RelationshipsResponse, metrics: MetricsResponse) => {
        console.log('Data model inference complete:', { entities, relationships, metrics });
        setInferredEntities(entities);
        setInferredRelationships(relationships);
        setInferredMetrics(metrics);
        setDataModelInferenceComplete(true);
    }, []);

    const handleAiInferenceLoadingChange = useCallback((isLoading: boolean) => {
        setAiInferenceLoading(isLoading);
    }, []);

    

    // Reset data model inference completion when step changes
    useEffect(() => {
        if (step !== 5 && step !== 6) {
            setDataModelInferenceComplete(false);
            setDataModelSetupOption(null);
            setAiInferenceLoading(false);
            setImportLoading(false);
            setImportComplete(false);
            setImportError(null);
            setImportJobId(null);
            setInferredEntities(null);
            setInferredRelationships(null);
            setInferredMetrics(null);
        }
    }, [step]);

    const nextButtonIsActive = useMemo(() => {
        switch (step) {
            case 1:
                return useCase !== '';
            case 2:
                if (useCase === 'MODELED_DATA') {
                    return !!snowflakeDetails.account_identifier;
                } else if (useCase === 'NO_SNOWFLAKE') {
                    return snowflakeManagement !== undefined;
                }
                return false;
            case 3:
                if (useCase === 'MODELED_DATA') {
                    return snowflakeConnectionIsValid;
                }
                return false;
            case 4:
                if (useCase === 'MODELED_DATA') {
                    return selectedTables.length > 0;
                }
                return false;
            case 5:
                // Enable next button if:
                // (a) Manual setup is selected, OR
                // (b) AI setup is selected AND AI has finished loading (not currently loading and inference is complete)
                return dataModelSetupOption === 'manual' || 
                       (dataModelSetupOption === 'ai' && !aiInferenceLoading && dataModelInferenceComplete);
            case 6:
                if (useCase === 'MODELED_DATA') {
                    // Enable next button if import is complete
                    return importComplete;
                }
                return false;
            // Add more cases for other steps as needed
            default:
                return false;
        }
    }, [step, useCase, snowflakeManagement, snowflakeDetails, selectedTables, dataModelInferenceComplete, dataModelSetupOption, aiInferenceLoading, importComplete, importError]);

    const [loading, setLoading] = useState(false);
    const [tenantConfigId, setTenantConfigId] = useState('');
    const [savingConfig, setSavingConfig] = useState(false);
    const [saveConfigError, setSaveConfigError] = useState('');
    const [configSaved, setConfigSaved] = useState(false);

    const [answers, setAnswers] = useImmer<SurveyAnswers>({
        
    });

    const [tenantInfraIsReady, setTenantInfraIsReady] = useState(false);
    const [error, setError] = useState('');



   
    const parseAccountIdentifierFromHost = useCallback((host: string) => {
        const match = host.match(/^https:\/\/([a-zA-Z0-9\-\_]+)\.snowflakecomputing\.com$/);
        if (match) {
            return match[1];
        }
        return null;
    }, []);

    // Load their existing config so we can pre-populate the form
    const loadData = async () => {
        setLoading(true);
        try {
            const {
                tenantRegistration,
                tenant_config,
                connection_creds,
                loader_creds
            } : any = await TenantService.getInstance().getTenantRegistration();
    
            setTenantConfigId(tenant_config.id);
            if (tenant_config.onboarding_info) {
                setPliableUseCase(tenant_config.onboarding_info.pliableUseCase || []);
                setPliableUseCaseOther(tenant_config.onboarding_info.pliableUseCaseOther || '');

            }
            setTenantInfraIsReady(tenant_config.infra.is_ready);
            setSnowflakeConnectionIsValid(tenant_config.infra.is_ready);

            setSnowflakeDetails({
                
                private_key: connection_creds.private_key || '',
                private_key_passphrase: connection_creds.private_key_passphrase || '',
                account_identifier: parseAccountIdentifierFromHost(connection_creds.host) || '',
                username: connection_creds.username,
                database: connection_creds.database,
                role: connection_creds.role,
                query_warehouse: connection_creds.query_warehouse,
                loading_warehouse: loader_creds.loading_warehouse,
                build_warehouse: connection_creds.build_warehouse,
                loader_role: loader_creds.role,
                loader_username: loader_creds.username,
                loader_private_key: loader_creds.private_key || '',
                loader_private_key_passphrase: loader_creds.private_key_passphrase || '',
                loader_database: loader_creds.database,
                
            });
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
        
    }

    useEffect(() => {
        loadData();
    }, []);

    const setSnowflakeValue = (key: keyof SnowflakeDetails, value: string) => {
        setSnowflakeDetails(produce(snowflakeDetails, draft => {
            draft[key] = value;
        }));
    }

    const saveSnowflakeDetails = useCallback(async () => {
        setSavingConfig(true);

        try {
            await TenantConfigService.getInstance().saveSnowflakeDetails(tenantConfigId, snowflakeDetails);
            setConfigSaved(true);
        } catch (err) {
            setSaveConfigError(getErrorMessage(err));
        } finally {
            setSavingConfig(false);
        }
        
    }, [snowflakeDetails, tenantConfigId]);
    const [runningBuild, setRunningBuild] = useState(false);
    const navigate = useNavigate();
    const executeFirstBuild = useCallback(async () => {
        setRunningBuild(true);
        await runBuild(
            '', 
            false, 
            true,
            undefined,
            undefined,
            false,
            () => {
                toast('success', 'Build Complete', 'Your build has completed successfully. Now you can continue setting up your data model.');
                setTimeout(() => {
                    navigate('/model/visual?autoLayout=1');
                    closeBuildModal();
                }, 1000);
            },
            () => {
                toast('danger', 'Build Failed', "Oops! Something went wrong with your build. Taking you to your dashboard so you can troubleshoot from there.");
                setTimeout(() => {
                    navigate('/');
                    closeBuildModal();
                }, 1000);
            }
        );
        setRunningBuild(false);
    }, []);
    
    return <div>
        <div className="p-onboarding">
            <h1>Welcome to Pliable.</h1>
            <p>Pliable is the AI and Analytics accelerator for Snowflake. Let's get you set up.</p>
            {step > 1 && (
                <div
                    style={{
                        background: '#f0f6fa',
                        border: '1px solid var(--pliable-blue)',
                        borderRadius: '10px',
                        padding: '1rem 1.5rem',
                        marginBottom: '2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <div>
                        <strong>Use Case:</strong>{' '}
                        {useCase === 'MODELED_DATA' && 'I have snowflake and I already have modeled data'}
                        {useCase === 'MESSY_DATA' && 'I have snowflake, but my data is a mess.'}
                        {useCase === 'NO_SNOWFLAKE' && "I don't have Snowflake"}
                    </div>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        style={{ marginLeft: '1rem' }}
                        onClick={() => setStep(1)}
                    >
                        Edit
                    </button>
                </div>
            )}
            <hr />
            {step == 1 && <>
                <h2>First, a few questions about you and your use case.</h2>
                <p>We'll use this information to help get you set up and recommend the best path forward.</p>
                {/* <div className="card mb-3">
                    <div className="card-body">
                        <h4>What are you hoping to get out of Pliable? Check all that apply.</h4>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                            gap: '1.5rem',
                            marginBottom: '2rem'
                        }}
                    >
                        <UseCaseButton
                            text="I want to build a semantic layer for my data"
                            active={pliableUseCase.includes('SEMANTIC_LAYER')}
                            iconClass="mdi mdi-database"
                            onClick={() =>
                                setPliableUseCase(prev =>
                                    prev.includes('SEMANTIC_LAYER')
                                        ? prev.filter(u => u !== 'SEMANTIC_LAYER')
                                        : [...prev, 'SEMANTIC_LAYER']
                                )
                            }
                            small
                        />
                        <UseCaseButton
                            text="I want simple business-wide analytics and dashboards"
                            active={pliableUseCase.includes('ANALYTICS_DASHBOARDS')}
                            iconClass="mdi mdi-chart-box"
                            onClick={() =>
                                setPliableUseCase(prev =>
                                    prev.includes('ANALYTICS_DASHBOARDS')
                                        ? prev.filter(u => u !== 'ANALYTICS_DASHBOARDS')
                                        : [...prev, 'ANALYTICS_DASHBOARDS']
                                )
                            }
                            small
                        />
                        <UseCaseButton
                            text="I want help using Snowflake."
                            active={pliableUseCase.includes('HELP_WITH_SNOWFLAKE')}
                            iconClass="mdi mdi-help-box"
                            onClick={() =>
                                setPliableUseCase(prev =>
                                    prev.includes('HELP_WITH_SNOWFLAKE')
                                        ? prev.filter(u => u !== 'HELP_WITH_SNOWFLAKE')
                                        : [...prev, 'HELP_WITH_SNOWFLAKE']
                                )
                            }
                            small
                        />
                        <UseCaseButton
                            text="I want to build a chatbot for my data"
                            active={pliableUseCase.includes('CHATBOT')}
                            iconClass="mdi mdi-robot"
                            onClick={() =>
                                setPliableUseCase(prev =>
                                    prev.includes('CHATBOT')
                                        ? prev.filter(u => u !== 'CHATBOT')
                                        : [...prev, 'CHATBOT']
                                )
                            }
                            small
                        />
                        <UseCaseButton
                            text="I want a single source of truth for my data and metrics"
                            active={pliableUseCase.includes('SINGLE_SOURCE_OF_TRUTH')}
                            iconClass="mdi mdi-star"
                            onClick={() =>
                                setPliableUseCase(prev =>
                                    prev.includes('SINGLE_SOURCE_OF_TRUTH')
                                        ? prev.filter(u => u !== 'SINGLE_SOURCE_OF_TRUTH')
                                        : [...prev, 'SINGLE_SOURCE_OF_TRUTH']
                                )
                            }
                            small
                        />
                        <UseCaseButton
                            text="Other (please specify)"
                            active={pliableUseCase.includes('OTHER')}
                            onClick={() =>
                                setPliableUseCase(prev =>
                                    prev.includes('OTHER')
                                        ? prev.filter(u => u !== 'OTHER')
                                        : [...prev, 'OTHER']
                                )
                            }
                            small
                        />
                        
                    </div>
                    {pliableUseCase.includes('OTHER') && (
                            <div style={{ marginTop: 8 }}>
                                <textarea
                                    className="form-control"
                                    placeholder="Please describe your use case"
                                    value={pliableUseCaseOther || ''}
                                    onChange={e => setPliableUseCaseOther(e.target.value)}
                                    rows={2}
                                />
                            </div>
                        )}
                    </div>
                </div> */}
                <div className="card">
                    <div className="card-body">
                        <h4>How would you describe your existing Snowflake setup?</h4>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                                gap: '1.5rem',
                                marginTop: 8,
                                marginBottom: 8,
                            }}
                        >
                            <UseCaseButton
                                text="I have snowflake and I already have modeled data"
                                active={useCase === 'MODELED_DATA'}
                                onClick={() => setUseCase('MODELED_DATA')}
                                small
                            />
                            <UseCaseButton
                                text="I have snowflake, but my data is a mess."
                                active={useCase === 'MESSY_DATA'}
                                onClick={() => setUseCase('MESSY_DATA')}
                                small
                            />
                            <UseCaseButton
                                text="I don't have Snowflake"
                                active={useCase === 'NO_SNOWFLAKE'}
                                onClick={() => setUseCase('NO_SNOWFLAKE')}
                                small
                            />
                        </div>
                    </div>
                </div>
                
            </>}
            {step > 1 && useCase == 'MODELED_DATA' && <>
                {step <=3 && <>
                    <h2>Great! We can import your modeled data right into Pliable's semantic layer. You'll be chatting with it in no time.</h2>
                    <p className="text-18">Let's just get connected to your Snowflake instance.</p>
                    <div className="card">
                        <div className="card-body">
                            <SnowflakeSetupForm
                                snowflakeDetails={snowflakeDetails}
                                onChange={setSnowflakeValue}
                                credsAreValid={snowflakeConnectionIsValid}
                                tenantConfigId={tenantConfigId}
                                showPliableHostedSnowflakeCode={false}
                                onCredentialsAreValid={(areValid: boolean) => {
                                    setSnowflakeConnectionIsValid(areValid);
                                }}
                                step={snowflakeSetupStep}
                            />
                        </div>

                    </div>
                    
                </>}
                {step == 4 && <>
                    <h2>Now let's import your existing data.</h2>
                    <p className="text-18">Select the tables you want to use in your Pliable Semantic Layer. Pliable can only see tables you explicitly grant access to. You can do this by running a script in your Snowflake account.</p>
                    <div className="card">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-6 border-right">
                                    <SnowflakeTableSelector
                                        multiMode
                                        onSelect={setSelectedTables}
                                        selectedTables={selectedTables}
                                        hideHelp
                                        header="Select Tables to Import"
                                        showSelections
                                    />
                                </div>
                                <div className="col-6">
                                    <SnowflakeGrantScript
                                        role={snowflakeDetails.role}
                                        database={snowflakeDetails.loader_database || ''}
                                    />
                                </div>

                            </div>
                            
                        </div>
                    </div>
                </>}
                {step == 5 && <div>
                   
                        
                    <h2>How would you like to set up your data model?</h2>
                    <div className="row">
                        <div className="col-12 col-md-6 mb-3">
                            <UseCaseButton
                                text="Manually Setup Data Model"
                                active={dataModelSetupOption === 'manual'}
                                onClick={() => {
                                    setDataModelSetupOption('manual');
                                }}
                                iconClass="mdi mdi-cog"
                            />
                        </div>
                        <div className="col-12 col-md-6 mb-3">
                            <UseCaseButton
                                text="Use AI to Infer Data Model"
                                active={dataModelSetupOption === 'ai'}
                                onClick={() => setDataModelSetupOption('ai')}
                                iconClass="mdi mdi-robot"
                            />
                        </div>
                    </div>
                    <hr />
                    {dataModelSetupOption === 'ai' && (
                        <>
                            <h2>Using AI to infer your data model...</h2>
                            <p className="text-18">This may take a few minutes if you selected a lot of tables. You can change what the AI suggests on the next screen.</p>
                            <DataModelInference 
                                tables={selectedTables} 
                                onLoadComplete={handleDataModelInferenceComplete}
                                onLoadingChange={handleAiInferenceLoadingChange}
                            />
                        </>
                    )}
                    {dataModelSetupOption === 'manual' && (
                        <>
                            <h2>Manual Data Model Setup</h2>
                            <p className="text-18">You've chosen to set up your data model manually. Click Next to begin importing your selected tables.</p>
                        </>
                    )}
                    </div>}
                {step == 6 && (dataModelSetupOption === 'manual' || dataModelSetupOption === 'ai') && (
                    <div>
                        <h2>Setting up your data model...</h2>
                        <p className="text-18">
                            {dataModelSetupOption === 'ai' 
                                ? "We're importing your selected tables and applying AI-suggested data model changes. This may take a few minutes."
                                : "We're importing your selected tables and setting up the data model. This may take a few minutes."
                            }
                        </p>
                        
                        <div className="card">
                            <div className="card-body">
                                {importLoading && (
                                    <div className="text-center">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                        <p className="mt-3">Importing {selectedTables.length} table{selectedTables.length !== 1 ? 's' : ''}...</p>
                                        {importJobId && (
                                            <small className="text-muted">Job ID: {importJobId}</small>
                                        )}
                                    </div>
                                )}
                                
                                {importComplete && !importError && (
                                    <div className="text-center text-success">
                                        <i className="mdi mdi-check-circle" style={{ fontSize: '3rem' }}></i>
                                        <h4 className="mt-3">Import Complete!</h4>
                                        <p>Your data model has been successfully set up with {selectedTables.length} table{selectedTables.length !== 1 ? 's' : ''}.</p>
                                    </div>
                                )}
                                
                                {importError && (
                                    <div className="text-center text-danger">
                                        <i className="mdi mdi-alert-circle" style={{ fontSize: '3rem' }}></i>
                                        <h4 className="mt-3">Import Failed</h4>
                                        <p>{importError}</p>
                                        <button 
                                            className="btn btn-outline-primary"
                                            onClick={() => {
                                                setImportError(null);
                                                setImportComplete(false);
                                                runDataModelImport();
                                            }}
                                        >
                                            Try Again
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {step == 7 && <>
                    <h2>Great! Now we just need to run your first build.</h2>
                    <p className="text-18">When you run a build in Pliable, the system will check for any tables that are new, or have changed since the last build. Those tables, as well as everything downstream of them, will be reprocessed according to their current configuration.</p>
                    <AsyncButton loading={runningBuild} className="btn btn-primary" onClick={executeFirstBuild} text="Run Build" icon="mdi mdi-hammer" />
                </>}
            </>}

            {step > 1 && useCase == 'NO_SNOWFLAKE' && <>
                {step == 2 && <>
                    <h2>No Snowflake? No Problem. Pliable can spin up and manage a Snowflake instance for you.</h2>
                    <div className="row">
                        <div className="col-12 col-md-6 mb-3">
                            <UseCaseButton
                                text="I want Pliable to manage my Snowflake instance"
                                active={snowflakeManagement === true}
                                onClick={() => {
                                    setSnowflakeManagement(true);
                                }}
                            />
                        </div>
                        <div className="col-12 col-md-6 mb-3">
                            <UseCaseButton
                                text="I'll sign up for Snowflake on my own."
                                active={snowflakeManagement === false}
                                onClick={() => {
                                    setSnowflakeManagement(false);
                                }}
                            />
                        </div>
                    </div>
                </>}
                {step == 3 && snowflakeManagement === true && <>
                    <h2>Great! We can handle everything.</h2>
                    <p className="text-18">Let's book a call to get you set up.</p>


                </>}
            </>}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
            <button
                type="button"
                className="btn btn-outline-secondary btn-huge"
                disabled={step <= 1}
                onClick={() => step > 1 && setStep(step - 1)}
            >
                &larr; Previous
            </button>
            <button
                type="button"
                className="btn btn-pliable btn-huge"
                disabled={!nextButtonIsActive}
                onClick={async () => {
                    if (nextButtonIsActive) {
                        // If moving from step 5 to step 6 with manual setup, trigger the import
                        
                        setStep(step + 1);
                    }
                }}
            >
                Next &rarr;
            </button>
        </div>
        </div>
    </div>
}

export default Onboarding2Page;